import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "./ResearchShowcase.css";

const findings = [
  {
    id: "01",
    value: "25",
    suffix: "×",
    tag: "Proactive Support",
    label: "More expensive to handle reactively",
    source: "Harvard Business Review · 1-5-25 Rule",
    body: (
      <>
        It costs <mark className="marker marker-yellow">$1 to answer proactively</mark>,
        $5 via chatbot, and $25 through a human agent. Most teams still rely on
        reactive queues and burn budget on every preventable conversation.
      </>
    ),
  },
  {
    id: "02",
    value: "96",
    suffix: "%",
    tag: "Friction Detection",
    label: "Of user friction is never reported",
    source: "Accenture Global Study · Silence Problem",
    body: (
      <>
        For every customer who complains,{" "}
        <mark className="marker marker-coral">26 remain silent</mark> and quietly
        churn. Ticket volume alone hides most of the friction your users actually
        experience.
      </>
    ),
  },
  {
    id: "03",
    value: "3.4",
    suffix: "×",
    tag: "Churn Mitigation",
    label: "Higher probability of churn",
    source: "HBR · Slow Support Hidden Costs",
    body: (
      <>
        Resolution times above four hours correlate with a{" "}
        <mark className="marker marker-purple">3.4× increase in churn risk</mark>.
        Slow support compounds into account loss faster than most teams expect.
      </>
    ),
  },
];

export default function ResearchShowcase() {
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) el.classList.add("in-view");
        });
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="research-section" id="research-showcase">
      <div className="research-shell" ref={stageRef}>
        <header className="research-head">
          <span className="research-eyebrow">The Data Behind the Product</span>
          <h2 className="research-heading">Why teams choose SmushChat.</h2>
          <p className="research-subhead">
            Three research-backed metrics that explain the ROI fast.
          </p>
        </header>

        <div className="research-metrics">
          {findings.map((finding) => (
            <article key={finding.id} className="metric-card">
              <div className="metric-top">
                <span className="metric-index">Signal {finding.id}</span>
                <span className="metric-tag">{finding.tag}</span>
              </div>

              <div className="metric-value" aria-hidden="true">
                {finding.value}
                <span className="metric-suffix">{finding.suffix}</span>
              </div>

              <p className="metric-label">{finding.label}</p>
              <p className="metric-body">{finding.body}</p>
              <span className="metric-source">{finding.source}</span>
            </article>
          ))}
        </div>

        <div className="research-footer">
          <Link to="/research" className="research-cta">
            Read full research <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
